/**
 * PandaPRO — Structured Logger
 *
 * Padrão único de logs para todos os fluxos críticos da aplicação.
 * - Nunca loga dados sensíveis (tokens, senhas, PINs, números de cartão)
 * - Formato consistente: [PandaPRO:flow] { ts, status, uid, entity, rid, msg, ctx, err, ms }
 * - Em produção: só emite error/warning. Em dev: emite tudo.
 * - Nunca lança exceção — log failure nunca deve quebrar o fluxo principal
 */

export type FlowStatus = 'start' | 'success' | 'error' | 'warning';

export interface LogPayload {
  /** Nome do fluxo (camelCase, e.g. 'createTransaction', 'payCardInvoice') */
  flow: string;
  /** Status da etapa sendo logada */
  status: FlowStatus;
  /** user_id do usuário autenticado — truncado automaticamente */
  userId?: string | null;
  /** Entidade afetada (e.g. 'transaction', 'transfer', 'card', 'goal') */
  entity?: string;
  /** ID do registro afetado — truncado automaticamente */
  recordId?: string;
  /** Mensagem objetiva descrevendo o evento */
  message?: string;
  /** Contexto adicional mínimo e útil (sem dados sensíveis) */
  context?: Record<string, unknown>;
  /** Objeto de erro — sanitizado automaticamente */
  error?: unknown;
  /** Duração em ms (use startTimer()) */
  durationMs?: number;
}

// ── Campos que NUNCA devem aparecer nos logs ──────────────────────────────────
const SENSITIVE_KEYS = new Set([
  'password', 'senha', 'token', 'secret', 'authorization',
  'pin', 'pin_hash', 'card_number', 'cvv', 'secret_key',
  'access_token', 'refresh_token', 'cpf', 'document',
  'bank_account_number', 'account_number', 'branch_number',
]);

function sanitize(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (SENSITIVE_KEYS.has(k.toLowerCase())) {
      out[k] = '[REDACTED]';
    } else if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = sanitize(v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function formatError(error: unknown): string {
  if (!error) return '';
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null) {
    const e = error as Record<string, unknown>;
    return String(e.message ?? e.error ?? JSON.stringify(error));
  }
  return String(error);
}

/** Trunca UID para privacy: mostra apenas os primeiros 8 chars + '...' */
const truncateId = (id?: string | null): string | undefined =>
  id ? id.slice(0, 8) + '...' : undefined;

const isDev = import.meta.env.DEV;

/**
 * Emite um log estruturado.
 * Nunca lança exceção — falha silenciosa se o console não estiver disponível.
 */
export const log = (payload: LogPayload): void => {
  try {
    const { flow, status, userId, entity, recordId, message, context, error, durationMs } = payload;

    const entry: Record<string, unknown> = {
      ts: new Date().toISOString(),
      flow,
      status,
    };

    const uid = truncateId(userId);
    if (uid)       entry.uid     = uid;
    if (entity)    entry.entity  = entity;
    const rid = truncateId(recordId);
    if (rid)       entry.rid     = rid;
    if (message)   entry.msg     = message;
    if (durationMs != null) entry.ms = durationMs;
    if (context)   entry.ctx     = sanitize(context);
    if (error)     entry.err     = formatError(error);

    const prefix = `[PandaPRO:${flow}]`;

    if (status === 'error') {
      console.error(prefix, entry);
    } else if (status === 'warning') {
      console.warn(prefix, entry);
    } else if (isDev) {
      // Em dev, loga tudo (start + success)
      console.log(prefix, entry);
    }
    // Em produção: success/start são silenciosos para reduzir ruído
  } catch {
    // Logger nunca quebra o fluxo principal
  }
};

/**
 * Retorna uma função que, ao ser chamada, retorna o tempo decorrido em ms.
 *
 * Uso:
 * ```ts
 * const elapsed = startTimer();
 * // ... operação ...
 * log({ flow: 'x', status: 'success', durationMs: elapsed() });
 * ```
 */
export const startTimer = (): (() => number) => {
  const t0 = performance.now();
  return () => Math.round(performance.now() - t0);
};

/**
 * Atalho para logar erro de forma consistente.
 * Inclui automaticamente status: 'error'.
 */
export const logError = (
  flow: string,
  message: string,
  opts?: Partial<Omit<LogPayload, 'flow' | 'status' | 'message'>>
): void => log({ flow, status: 'error', message, ...opts });

/**
 * Atalho para logar sucesso de forma consistente.
 */
export const logSuccess = (
  flow: string,
  message: string,
  opts?: Partial<Omit<LogPayload, 'flow' | 'status' | 'message'>>
): void => log({ flow, status: 'success', message, ...opts });
